import { useNavigate } from "@tanstack/react-router";
import { Filter, Image, Loader2, Replace, Search, Video } from "lucide-react";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { conversationAPI, filesystemAPI, slidesAPI, templateAPI } from "../../services/api";
import { mediaService } from "../../services/mediaService";
import { MediaPhoto, MediaVideo } from "../../services/mediaProvider";
import slidePathMapping from "../../services/slideMapping";
import { useMessage } from "../../hooks/useMessage";
import { streamManager } from "../../services/streamManager";
import { slideLoadingOptimizer } from "../../services/slideLoadingOptimizer";
import { conversationUpdatesService } from "../../services/conversationUpdatesService";
import { cacheVersionService } from "../../services/cacheVersionService";
import { DEFAULT_SLIDE_RESOLUTION } from "../../config/gsapViewer";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { GsapSlidePreviewEnhanced } from "../slides/GsapSlidePreviewEnhanced";
import { UnifiedSlide } from "../../types/gsapSlide";
import { detectSlideFormat, parseGsapSlide } from "../../utils/slideTypeDetector";
import { useUserStore } from "../../stores/userStore";

interface Slide {
  id: string;
  number: string;
  title: string;
  path: string;
  content: string;
}

interface SlideInfo {
  scene_id: string;
  title: string;
  layout_type: string;
  content_description: string;
  facts_used: string[];
  message: string;
  component_mapping: Record<string, string>;
  script_text: string;
  sync_points: string[];
  visual_instructions?: string;
  estimated_duration: number;
  // New comprehensive asset tracking
  generated_assets?: Array<{
    asset_component_id: string;
    asset_url: string;
    asset_prompt: string;
  }>;
  // Legacy format support
  generated_images?: string[];
  image_urls?: Array<{
    image_id: string;
    image_url: string;
  }>;
  video_urls?: Array<{
    video_id: string;
    video_url: string;
  }>;
  start_time?: number;
  end_time?: number;
  word_wise_timings?: Array<{
    word: string;
    start_time: number;
    end_time: number;
    confidence: number;
  }>;
  generation_metadata?: {
    last_modified: string;
    timing_updated?: string;
  };
}

interface SlidePreviewProps {
  conversationId?: string;
  selectedSlidePath?: string;
  onBackToGallery?: () => void;
  onSlideChange?: (slidePath: string) => void;
  className?: string;
  cachedSlides?: Slide[];
  cachedCurrentSlide?: Slide;
  isTemplate?: boolean;
  templateSlideJsonUrl?: string;
  resolution?: { width: number; height: number };
}


export const SlidePreview: React.FC<SlidePreviewProps> = ({
  conversationId,
  selectedSlidePath,
  onBackToGallery,
  className = "",
  cachedSlides,
  cachedCurrentSlide,
  isTemplate = false,
  templateSlideJsonUrl,
  resolution = DEFAULT_SLIDE_RESOLUTION,
}) => {
  const activeTeamId = useUserStore((s) => s.activeTeam?.id);
  const [slide, setSlide] = useState<Slide | null>(cachedCurrentSlide || null);
  const [slideInfo, setSlideInfo] = useState<SlideInfo | null>(null);
  const [allSlides, setAllSlides] = useState<Slide[]>(cachedSlides || []);
  const [loading, setLoading] = useState(!cachedSlides);
  const [infoLoading, setInfoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gsapSlide, setGsapSlide] = useState<UnifiedSlide | null>(null);
  const [isGsapSlide, setIsGsapSlide] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  // Pre-calculate initial scale to avoid jank
  const getInitialScale = () => {
    if (typeof window === 'undefined') return 0.5;
    // Use viewport dimensions for initial calculation
    const width = window.innerWidth * 0.9; // Account for padding
    const height = window.innerHeight * 0.9;
    return Math.min(width / resolution.width, height / resolution.height);
  };
  
  const [scale, setScale] = useState(getInitialScale);
  const [iframeReady, setIframeReady] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<"info" | "assets">("info");
  const [selectedAsset, setSelectedAsset] = useState<{
    asset: MediaPhoto | MediaVideo;
    type: "image" | "video";
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"all" | "images" | "videos">("all");
  const [searchResults, setSearchResults] = useState<
    (MediaPhoto | MediaVideo)[]
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [replacingAsset, setReplacingAsset] = useState<{
    id: string;
    type: "image" | "video";
  } | null>(null);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [orientation, setOrientation] = useState<"all" | "landscape" | "portrait" | "square">("all");
  const [color, setColor] = useState<string>("all");
  const modalScrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { sendMessage } = useMessage();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const refreshDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Handle back to gallery navigation
  const handleBackToGallery = () => {
    if (conversationId) {
      navigate({
        to: "/chat/$conversationId/gallery",
        params: {
          conversationId,
        },
        search: { slide: undefined, tab: undefined },
      });
    } else if (onBackToGallery) {
      onBackToGallery();
    }
  };

  // Function to reload slide content with cache busting
  const reloadSlideContent = useCallback(async () => {
    if (!selectedSlidePath || !conversationId) return;

    // Skip reload for templates - they're static public files
    if (isTemplate) {
      console.log('ℹ️ Skipping reload for template slide');
      return;
    }

    try {
      setIsRefreshing(true);
      console.log('🔄 Reloading slide content:', selectedSlidePath);

      // Force cache invalidation
      await cacheVersionService.getFileVersion(
        selectedSlidePath,
        conversationId,
        { forceRefresh: true }
      );

      // Re-fetch the slide content
      const response = await filesystemAPI.read(selectedSlidePath, conversationId);

      if (response.content) {
        // Check if content actually changed to avoid unnecessary refreshes
        if (slide && slide.content === response.content) {
          console.log('ℹ️ Slide content unchanged, skipping refresh');
          setIsRefreshing(false);
          return;
        }

        // Update slide content
        setSlide(prev => prev ? { ...prev, content: response.content } : null);

        // Smooth iframe refresh by keeping old content visible during transition
        if (iframeRef.current) {
          // Direct update without unmounting iframe for smoother experience
          iframeRef.current.srcdoc = response.content;
          setLastUpdated(new Date());
          console.log('✅ Slide content refreshed successfully via direct update');
        } else {
          // Fallback to remounting iframe if ref not available
          setIframeReady(false);
          requestAnimationFrame(() => {
            setIframeReady(true);
            setLastUpdated(new Date());
          });
          console.log('✅ Slide content refreshed successfully via remount');
        }
      }
    } catch (error) {
      console.error('❌ Error refreshing slide content:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [selectedSlidePath, conversationId, isTemplate]);

  // Listen for conversation completion to trigger reload
  useEffect(() => {
    if (!conversationId || !selectedSlidePath) {
      return;
    }

    const handleStreamComplete = (event: CustomEvent) => {
      console.log('🎯 Stream complete event received:', event.detail);
      const { conversationId: eventConvId } = event.detail || {};

      // Check if this is for our conversation
      if (eventConvId === conversationId) {
        console.log('✨ AI finished updating slide, auto-reloading in 1 second...');

        // Wait a bit for file writes to complete, then reload
        setTimeout(() => {
          console.log('🔄 Auto-reloading slide after AI completion');
          reloadSlideContent();
        }, 1000);
      }
    };

    // Listen for stream completion events
    window.addEventListener('stream-message_complete', handleStreamComplete as EventListener);

    return () => {
      window.removeEventListener('stream-message_complete', handleStreamComplete as EventListener);
    };
  }, [conversationId, selectedSlidePath, reloadSlideContent]);

  // Auto-reload slide when it's opened/changed
  useEffect(() => {
    if (!selectedSlidePath) {
      return;
    }

    // Reload slide content when opening or switching slides
    console.log('🔄 Auto-reloading slide on mount/change:', selectedSlidePath);

    // Small delay to ensure everything is ready
    const reloadTimer = setTimeout(() => {
      reloadSlideContent();
    }, 500);

    return () => clearTimeout(reloadTimer);
  }, [selectedSlidePath]); // Only depend on slide path change

  // Listen for real-time slide updates via SSE
  useEffect(() => {
    if (!conversationId || !selectedSlidePath) {
      console.log('⚠️ SlidePreview SSE: Missing required props', { conversationId, selectedSlidePath });
      return;
    }

    console.log('🚀 SlidePreview SSE: Starting setup for', { conversationId, selectedSlidePath });

    const setupRealtimeUpdates = async () => {
      // Extract slide number from current path
      const extractSlideNumber = (path: string): number | null => {
        const match = path.match(/slide-(\d+)/);
        return match ? parseInt(match[1], 10) : null;
      };

      const currentSlideNumber = extractSlideNumber(selectedSlidePath);

      console.log('👁️ Setting up real-time slide update listener for conversation:', conversationId);
      console.log('📍 Current slide path:', selectedSlidePath);
      console.log('📍 Current slide number:', currentSlideNumber);

      const teamId = activeTeamId;
      if (!teamId) {
        console.log('⚠️ SlidePreview SSE: no active team yet, skipping');
        return;
      }
      console.log('📍 Team ID for SSE:', teamId);

      // Start listening for SSE updates if not already listening
      if (!conversationUpdatesService.isConnected()) {
        console.log('🔌 Starting SSE connection for team:', teamId);
        conversationUpdatesService.startListening(teamId);

        // Wait a bit for connection to establish
        await new Promise(resolve => setTimeout(resolve, 100));
        console.log('🔌 SSE connection status:', conversationUpdatesService.isConnected());
      } else {
        console.log('✅ SSE already connected for team:', conversationUpdatesService.getCurrentTeamId());
      }

      // Subscribe to conversation updates
      const unsubscribe = conversationUpdatesService.onConversationUpdate(
        async (convId, updates) => {
          console.log('📨 SSE Event received for conversation:', convId, 'Updates:', updates);
          console.log('📨 Checking against our conversation:', conversationId);

          // Check both with and without conversation ID match
          // Sometimes events come without specific conversation ID for file system changes
          if (convId === conversationId || !convId) {
            console.log('🎯 Event is relevant to us');

            // Check for slide updates
            if (updates.slidesUpdated) {
              console.log('📝 Slides updated event detected');
              console.log('📝 Update details:', {
                updateSlideNumber: updates.slideNumber,
                currentSlideNumber,
                match: updates.slideNumber === currentSlideNumber
              });

              // Check if this update affects the current slide
              if (updates.slideNumber === currentSlideNumber || !updates.slideNumber) {
                console.log('🎯 Current slide is affected, refreshing...');

                // Debounce rapid updates (500ms)
                if (refreshDebounceRef.current) {
                  clearTimeout(refreshDebounceRef.current);
                }

                refreshDebounceRef.current = setTimeout(() => {
                  console.log('🔄 Executing slide refresh now');
                  reloadSlideContent();
                  refreshDebounceRef.current = null;
                }, 500);
              } else {
                console.log('⏭️ Update is for slide', updates.slideNumber, 'but current slide is', currentSlideNumber);
              }
            }

            // Also check for general file changes that might affect slides
            if (updates.fileChanged && selectedSlidePath.includes(updates.fileName)) {
              console.log('📁 File change detected for current slide');
              reloadSlideContent();
            }

            // Check for any update type that might affect slides
            if (updates.updateType || updates.filesUpdated) {
              console.log('📁 General update detected:', updates);
              // If it's a slide-related path, refresh
              if (selectedSlidePath && (updates.path?.includes('slide') || updates.fileName?.includes('slide'))) {
                console.log('🔄 Slide-related file changed, refreshing');
                reloadSlideContent();
              }
            }
          } else {
            console.log('⏭️ Event for different conversation:', convId);
          }
        }
      );

      console.log('✅ SSE subscription created successfully');

      // Cleanup function
      return () => {
        console.log('🧹 Cleaning up SSE subscription');
        unsubscribe();
        if (refreshDebounceRef.current) {
          clearTimeout(refreshDebounceRef.current);
        }
      };
    };

    const cleanup = setupRealtimeUpdates();

    // Cleanup on unmount
    return () => {
      cleanup.then(fn => fn && fn());
    };
  }, [conversationId, selectedSlidePath, reloadSlideContent, activeTeamId]);

  useEffect(() => {
    // Define fetchSlideInfo locally to avoid hoisting issues
    const fetchSlideInfoLocal = async (slidePath: string) => {
      try {
        setInfoLoading(true);
        
        // Use optimized slide loading with caching
        const useLazyLoading = localStorage.getItem('slidePreview.lazyLoading') !== 'false';
        
        if (useLazyLoading) {
          // Use the optimized loader
          const info = await slideLoadingOptimizer.loadSlideInfo(
            slidePath,
            conversationId,
            { useCache: true }
          );
          
          if (info) {
            const processedInfo = normalizeAssetInfo(info, slide?.content || '');
            setSlideInfo(processedInfo);
          } else {
            setSlideInfo(null);
          }
        } else {
          // Fallback to original behavior
          const slideDir = slidePath.substring(0, slidePath.lastIndexOf("/"));
          const infoPath = `${slideDir}/info.json`;

          const response = await filesystemAPI.read(infoPath);
          const info = JSON.parse(response.content);
          
          // Process and normalize asset information
          const processedInfo = normalizeAssetInfo(info, slide?.content || '');
          setSlideInfo(processedInfo);
        }
      } catch (err) {
        console.warn("Could not load scene info:", err);
        setSlideInfo(null);
      } finally {
        setInfoLoading(false);
      }
    };
    
    // Use cached data if available
    if (cachedSlides && cachedCurrentSlide) {
      console.log('SlidePreview - Using cached slide data, skipping fetch');
      setLoading(false);
      setAllSlides(cachedSlides);
      setSlide(cachedCurrentSlide);

      // Detect if this is a GSAP slide (same logic as fresh fetch)
      console.log('🔍 SlidePreview (cached) - Detecting slide format:', {
        path: cachedCurrentSlide.path,
        source: (cachedCurrentSlide as any).source,
        hasGsap: (cachedCurrentSlide as any).hasGsap
      });

      const slideFormat = detectSlideFormat(cachedCurrentSlide.path);
      const isGsap = slideFormat === 'gsap';

      console.log('🔍 SlidePreview (cached) - Detection result:', {
        slideFormat,
        isGsap,
        willSetGsapSlide: isGsap
      });

      setIsGsapSlide(isGsap);

      if (isGsap) {
        // Fetch and parse GSAP slide JSON
        (async () => {
          try {
            // Extract relative path for API call
            // Video slides already have relative paths: slides/slide-002/slide.json
            // Template slides have absolute paths: /path/slide_01/slide.json → slide_01/slide.json
            const slidePath = cachedCurrentSlide.path;
            let apiPath = slidePath;

            // Check if it's an absolute path (starts with /)
            if (slidePath && slidePath.startsWith('/')) {
              // Absolute path - need to extract relative part
              // For template projects: /path/slide_01/slide.json → slide_01/slide.json
              const templateMatch = slidePath.match(/\/(slide_\d+\/[^/]+)$/);
              if (templateMatch) {
                apiPath = templateMatch[1];
              }
            }
            // If it's already relative (video slides), use as-is

            console.log('📥 SlidePreview (cached) - Fetching GSAP slide content from:', apiPath);

            // For templates, fetch directly from the public URL
            let gsapContent;
            if (isTemplate && templateSlideJsonUrl) {
              console.log('📥 Loading template slide from:', templateSlideJsonUrl);
              const response = await fetch(templateSlideJsonUrl);
              if (!response.ok) {
                throw new Error(`Failed to fetch template: ${response.statusText}`);
              }
              gsapContent = await response.json();
            } else {
              gsapContent = await slidesAPI.getGsapSlide(apiPath, conversationId);
            }
            console.log('✅ SlidePreview (cached) - GSAP content fetched successfully');

            const parsedGsapSlide = await parseGsapSlide(
              cachedCurrentSlide.path,
              JSON.stringify(gsapContent),
              cachedCurrentSlide.number
            );
            console.log('✅ SlidePreview (cached) - GSAP slide parsed successfully:', parsedGsapSlide.id);

            // For templates, add slideJsonUrl to metadata to ensure correct API endpoint is used
            if (isTemplate && templateSlideJsonUrl) {
              parsedGsapSlide.metadata = {
                ...parsedGsapSlide.metadata,
                slideJsonUrl: templateSlideJsonUrl,
                isTemplate: true
              };
              console.log('✅ Added template metadata with slideJsonUrl:', templateSlideJsonUrl);
            }

            setGsapSlide(parsedGsapSlide);
          } catch (error) {
            console.error('❌ SlidePreview (cached) - Failed to load GSAP slide:', error);
            setError('Failed to load GSAP slide');
          }
        })();
      } else {
        // HTML slide - fetch slide info.json
        fetchSlideInfoLocal(cachedCurrentSlide.path);
      }

      return;
    }
    
    const fetchSlides = async () => {
      if (!conversationId || !selectedSlidePath) {
        setSlide(null);
        setAllSlides([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get conversation details to check if it's a style conversation
        const conversationResponse =
          await conversationAPI.getById(conversationId);
        const conversation = conversationResponse.conversation;
        const isStyleConversation =
          conversation.collectionType === "STYLE";

        let slides: Slide[] = [];

        if (isStyleConversation) {
          // For style conversations, fetch layout files and read their content
          try {
            const dirResponse = await filesystemAPI.browse(
              "style",
              conversationId,
            );

            if (dirResponse.files) {
              const layoutFiles = dirResponse.files.filter(
                (file: any) =>
                  file.type === "file" &&
                  file.name.startsWith("layout_") &&
                  file.name.endsWith(".html"),
              );

              // Read the HTML content for each layout file
              slides = await Promise.all(
                layoutFiles.map(async (file: any, index: number) => {
                  let htmlContent = `Layout: ${file.name}`;

                  try {
                    const fileResponse = await filesystemAPI.read(
                      file.path,
                      conversationId,
                    );
                    htmlContent = fileResponse.content;
                  } catch (error) {
                    console.warn(
                      `Could not read content for ${file.path}:`,
                      error,
                    );
                  }

                  return {
                    id: `layout-${index}`,
                    number: String(index + 1).padStart(3, "0"),
                    title: file.name
                      .replace("layout_", "")
                      .replace(".html", "")
                      .replace("_", " "),
                    path: file.path,
                    content: htmlContent,
                  };
                }),
              );
            }
          } catch (error) {
            console.warn(
              "Could not fetch layout files for style conversation:",
              error,
            );
          }
        } else {
          // For video conversations, fetch slides normally
          const data = await slidesAPI.getByConversation(conversationId);
          slides = data.slides || [];
        }

        setAllSlides(slides);

        const selectedSlide = slides.find(
          (s: Slide) => s.path === selectedSlidePath,
        );
        setSlide(selectedSlide || null);

        // Detect if this is a GSAP slide
        if (selectedSlide) {
          console.log('🔍 SlidePreview - Detecting slide format:', {
            path: selectedSlide.path,
            source: (selectedSlide as any).source,
            hasGsap: (selectedSlide as any).hasGsap
          });

          const slideFormat = detectSlideFormat(selectedSlide.path);
          const isGsap = slideFormat === 'gsap';

          console.log('🔍 SlidePreview - Detection result:', {
            slideFormat,
            isGsap,
            willSetGsapSlide: isGsap
          });

          setIsGsapSlide(isGsap);

          if (isGsap) {
            // Fetch and parse GSAP slide JSON
            try {
              // Extract relative path for API call
              // Video slides already have relative paths: slides/slide-002/slide.json
              // Template slides have absolute paths: /path/slide_01/slide.json → slide_01/slide.json
              const slidePath = selectedSlide.path;
              let apiPath = slidePath;

              // Check if it's an absolute path (starts with /)
              if (slidePath && slidePath.startsWith('/')) {
                // Absolute path - need to extract relative part
                // For template projects: /path/slide_01/slide.json → slide_01/slide.json
                const templateMatch = slidePath.match(/\/(slide_\d+\/[^/]+)$/);
                if (templateMatch) {
                  apiPath = templateMatch[1];
                }
              }
              // If it's already relative (video slides), use as-is

              console.log('📥 SlidePreview - Fetching GSAP slide content from:', apiPath);
              const gsapContent = await slidesAPI.getGsapSlide(apiPath, conversationId);
              console.log('✅ SlidePreview - GSAP content fetched successfully');

              const parsedGsapSlide = await parseGsapSlide(
                selectedSlide.path,
                JSON.stringify(gsapContent),
                selectedSlide.number
              );
              console.log('✅ SlidePreview - GSAP slide parsed successfully:', parsedGsapSlide.id);
              setGsapSlide(parsedGsapSlide);
            } catch (error) {
              console.error('❌ SlidePreview - Failed to load GSAP slide:', error);
              setError('Failed to load GSAP slide');
            }
          }
        }

        // Fetch slide info.json if slide is found (only for video conversations and HTML slides)
        if (selectedSlide && !isStyleConversation && !isGsapSlide) {
          fetchSlideInfoLocal(selectedSlide.path);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, [conversationId, selectedSlidePath, cachedSlides, cachedCurrentSlide]);

  // fetchSlideInfo is defined inside the useEffect to avoid hoisting issues
  
  /**
   * Normalize asset information from multiple possible sources
   * Supports new format (generated_assets), legacy formats, and HTML parsing
   */
  const normalizeAssetInfo = useCallback((info: any, slideHtml: string): SlideInfo => {
    const normalizedInfo = { ...info };
    
    // Initialize arrays if not present
    if (!normalizedInfo.image_urls) normalizedInfo.image_urls = [];
    if (!normalizedInfo.video_urls) normalizedInfo.video_urls = [];
    
    // 1. Process new format: generated_assets
    if (info.generated_assets && Array.isArray(info.generated_assets)) {
      info.generated_assets.forEach((asset: any) => {
        // Add to legacy format for UI compatibility
        if (asset.asset_url && (asset.asset_url.includes('.mp4') || asset.asset_url.includes('.webm'))) {
          // It's a video
          if (!normalizedInfo.video_urls.find((v: any) => v.video_url === asset.asset_url)) {
            normalizedInfo.video_urls.push({
              video_id: asset.asset_component_id,
              video_url: asset.asset_url
            });
          }
        } else {
          // It's an image
          if (!normalizedInfo.image_urls.find((img: any) => img.image_url === asset.asset_url)) {
            normalizedInfo.image_urls.push({
              image_id: asset.asset_component_id,
              image_url: asset.asset_url
            });
          }
        }
      });
    }
    
    // 2. Process generated_images field (from new slides)
    if (info.generated_images && Array.isArray(info.generated_images)) {
      info.generated_images.forEach((url: string, index: number) => {
        if (!normalizedInfo.image_urls.find((img: any) => img.image_url === url)) {
          normalizedInfo.image_urls.push({
            image_id: `generated_${index}`,
            image_url: url
          });
        }
      });
    }
    
    // 3. Process component_mapping for images/videos
    if (info.component_mapping) {
      Object.entries(info.component_mapping).forEach(([key, value]) => {
        if (typeof value === 'string' && value.startsWith('http')) {
          // Check if it's an image or video URL
          if (value.includes('.mp4') || value.includes('.webm')) {
            if (!normalizedInfo.video_urls.find((v: any) => v.video_url === value)) {
              normalizedInfo.video_urls.push({
                video_id: key,
                video_url: value
              });
            }
          } else if (key.toLowerCase().includes('image') || key.toLowerCase().includes('img') || 
                     value.includes('.png') || value.includes('.jpg') || value.includes('.jpeg')) {
            if (!normalizedInfo.image_urls.find((img: any) => img.image_url === value)) {
              normalizedInfo.image_urls.push({
                image_id: key,
                image_url: value
              });
            }
          }
        }
      });
    }
    
    // 4. Parse HTML content as fallback
    if (slideHtml) {
      // Extract images from img tags
      const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*(?:id=["']([^"']+)["'])?[^>]*(?:class=["']([^"']+)["'])?/gi;
      let match;
      while ((match = imgRegex.exec(slideHtml)) !== null) {
        const url = match[1];
        const id = match[2] || match[3] || `html_img_${normalizedInfo.image_urls.length}`;
        if (url.startsWith('http') && !normalizedInfo.image_urls.find((img: any) => img.image_url === url)) {
          normalizedInfo.image_urls.push({
            image_id: id,
            image_url: url
          });
        }
      }
      
      // Extract background images from style attributes
      const elementWithBgRegex = /<[^>]+(?:id=["']([^"']+)["'])?[^>]*(?:class=["']([^"']+)["'])?[^>]*style=["'][^"']*background-image:\s*url\(['"]?([^'")]+)['"]?\)/gi;
      
      while ((match = elementWithBgRegex.exec(slideHtml)) !== null) {
        const id = match[1] || match[2] || `html_bg_${normalizedInfo.image_urls.length}`;
        const url = match[3];
        if (url && url.startsWith('http') && !normalizedInfo.image_urls.find((img: any) => img.image_url === url)) {
          normalizedInfo.image_urls.push({
            image_id: id,
            image_url: url
          });
        }
      }
      
      // Extract videos
      const videoRegex = /<video[^>]+src=["']([^"']+)["'][^>]*(?:id=["']([^"']+)["'])?[^>]*(?:class=["']([^"']+)["'])?/gi;
      while ((match = videoRegex.exec(slideHtml)) !== null) {
        const url = match[1];
        const id = match[2] || match[3] || `html_video_${normalizedInfo.video_urls.length}`;
        if (url.startsWith('http') && !normalizedInfo.video_urls.find((v: any) => v.video_url === url)) {
          normalizedInfo.video_urls.push({
            video_id: id,
            video_url: url
          });
        }
      }
    }
    
    // Remove duplicates by URL
    normalizedInfo.image_urls = Array.from(
      new Map(normalizedInfo.image_urls.map((img: any) => [img.image_url, img])).values()
    );
    normalizedInfo.video_urls = Array.from(
      new Map(normalizedInfo.video_urls.map((vid: any) => [vid.video_url, vid])).values()
    );
    
    return normalizedInfo;
  }, []); // Empty deps as function doesn't use external values

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Update slide when selectedSlidePath changes (for navigation)
  useEffect(() => {
    if (selectedSlidePath && allSlides.length > 0) {
      const newSlide = allSlides.find(s => s.path === selectedSlidePath);
      if (newSlide && newSlide.path !== slide?.path) {
        console.log('SlidePreview - Updating slide for new path:', selectedSlidePath);
        setSlide(newSlide);
        setIframeReady(false);
        
        // Fetch slide info for the new slide
        if (conversationId) {
          const fetchSlideInfoLocal = async () => {
            try {
              setInfoLoading(true);
              const useLazyLoading = localStorage.getItem('slidePreview.lazyLoading') !== 'false';
              
              if (useLazyLoading) {
                const info = await slideLoadingOptimizer.loadSlideInfo(
                  newSlide.path,
                  conversationId,
                  { useCache: true }
                );
                
                if (info) {
                  const processedInfo = normalizeAssetInfo(info, newSlide.content || '');
                  setSlideInfo(processedInfo);
                } else {
                  setSlideInfo(null);
                }
              } else {
                const slideDir = newSlide.path.substring(0, newSlide.path.lastIndexOf("/"));
                const infoPath = `${slideDir}/info.json`;
                const response = await filesystemAPI.read(infoPath);
                const info = JSON.parse(response.content);
                const processedInfo = normalizeAssetInfo(info, newSlide.content || '');
                setSlideInfo(processedInfo);
              }
            } catch (err) {
              console.warn("Could not load scene info:", err);
              setSlideInfo(null);
            } finally {
              setInfoLoading(false);
            }
          };
          fetchSlideInfoLocal();
        }
      }
    }
  }, [selectedSlidePath, allSlides, conversationId, normalizeAssetInfo]);
  
  // Progressive iframe loading - load after component mount for smooth transition
  useEffect(() => {
    if (slide && !iframeReady) {
      // Use requestAnimationFrame for smooth loading
      const timer = requestAnimationFrame(() => {
        setIframeReady(true);
        console.log('SlidePreview - Progressive iframe loading triggered');
      });
      return () => cancelAnimationFrame(timer);
    }
  }, [slide, iframeReady]);

  // Scale calculation effect - same logic as SlideGallery
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        const scaleX = containerWidth / resolution.width;
        const scaleY = containerHeight / resolution.height;
        const optimalScale = Math.min(scaleX, scaleY);

        setScale(optimalScale);
      }
    };

    updateScale();
    window.addEventListener("resize", updateScale);

    // Use ResizeObserver for more accurate container size detection
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => {
        window.removeEventListener("resize", updateScale);
        resizeObserver.disconnect();
      };
    }

    return () => window.removeEventListener("resize", updateScale);
  }, [slide]); // Re-run when slide changes

  // Intersection observer for infinite scroll in modal
  useEffect(() => {
    if (!isReplaceModalOpen || !hasMoreResults || isLoadingMore || searchLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreResults();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [isReplaceModalOpen, hasMoreResults, isLoadingMore, searchLoading, searchPage]);

  // Auto-trigger animations after iframe loads
  useEffect(() => {
    if (!iframeReady || !slide) return;
    
    const iframe = iframeRef.current;
    if (!iframe) return;
    
    const triggerAnimations = () => {
      // Wait for iframe content to fully initialize
      setTimeout(() => {
        try {
          console.log('SlidePreview - Triggering animations for slide');
          iframe.contentWindow?.postMessage({ type: "startAnimations" }, "*");
        } catch (error) {
          console.debug("Could not send animation trigger to iframe:", error);
        }
      }, 300); // Give iframe time to fully initialize
    };
    
    // Check if iframe is already loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      triggerAnimations();
    } else {
      // Wait for iframe to load
      const handleLoad = () => {
        triggerAnimations();
      };
      
      iframe.addEventListener("load", handleLoad);
      return () => iframe.removeEventListener("load", handleLoad);
    }
  }, [iframeReady, slide]); // Re-run when iframe becomes ready or slide changes

  // Navigation functions
  const getCurrentSlideIndex = (): number => {
    if (!selectedSlidePath || allSlides.length === 0) return -1;
    return allSlides.findIndex((s) => s.path === selectedSlidePath);
  };

  const canGoToPrevious = (): boolean => {
    const currentIndex = getCurrentSlideIndex();
    return currentIndex > 0;
  };

  const canGoToNext = (): boolean => {
    const currentIndex = getCurrentSlideIndex();
    return currentIndex >= 0 && currentIndex < allSlides.length - 1;
  };

  const goToPrevious = useCallback(async () => {
    const currentIndex = getCurrentSlideIndex();
    if (currentIndex > 0 && conversationId) {
      const prevSlidePath = allSlides[currentIndex - 1].path;
      
      // Prefetch the previous slide's info while navigating
      if (currentIndex > 1) {
        slideLoadingOptimizer.prefetchSlide(allSlides[currentIndex - 2].path, conversationId);
      }
      
      const slideHash =
        await slidePathMapping.generateHashForPath(prevSlidePath);
      navigate({
        to: "/chat/$conversationId/slide/$slideHash",
        params: {
          conversationId,
          slideHash,
        },
        search: {
          slide: undefined,
          fromTab: undefined,
        },
      });
    }
  }, [allSlides, conversationId, navigate]);

  const goToNext = useCallback(async () => {
    const currentIndex = getCurrentSlideIndex();
    if (
      currentIndex >= 0 &&
      currentIndex < allSlides.length - 1 &&
      conversationId
    ) {
      const nextSlidePath = allSlides[currentIndex + 1].path;
      
      // Prefetch the next slide's info while navigating
      if (currentIndex < allSlides.length - 2) {
        slideLoadingOptimizer.prefetchSlide(allSlides[currentIndex + 2].path, conversationId);
      }
      
      const slideHash =
        await slidePathMapping.generateHashForPath(nextSlidePath);
      navigate({
        to: "/chat/$conversationId/slide/$slideHash",
        params: {
          conversationId,
          slideHash,
        },
        search: {
          slide: undefined,
          fromTab: undefined,
        },
      });
    }
  }, [allSlides, conversationId, navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Disable navigation when modal is open to prevent conflicts with text input
      if (isReplaceModalOpen) {
        if (event.key === "Escape") {
          setIsReplaceModalOpen(false);
        }
        return;
      }

      if (event.key === "ArrowLeft" && canGoToPrevious()) {
        goToPrevious();
      } else if (event.key === "ArrowRight" && canGoToNext()) {
        goToNext();
      } else if (event.key === "Escape") {
        handleBackToGallery();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [allSlides, selectedSlidePath, handleBackToGallery, isReplaceModalOpen]);

  const extractSlideNumber = (path: string): string => {
    const match = path.match(/slide-?(\d+)/i);
    return match ? match[1] : "0";
  };

  const extractTitle = (content: string): string => {
    const titleMatch = content.match(/<title>(.*?)<\/title>/i);
    if (titleMatch) {
      return titleMatch[1].replace(/^Slide \d+:\s*/, "");
    }

    const h1Match = content.match(/<h1[^>]*>(.*?)<\/h1>/i);
    return h1Match ? h1Match[1] : "Untitled Scene";
  };

  const handleAssetSelect = (
    asset: MediaPhoto | MediaVideo,
    type: "image" | "video",
  ) => {
    setSelectedAsset({ asset, type });
    // Placeholder for asset replacement logic
    console.log("Asset selected:", asset, type);
  };

  // Search media providers for assets with pagination
  const searchPexels = async (page = 1, append = false) => {
    const isAvailable = mediaService.getAvailableProviders().length > 0;
    if (!isAvailable || !searchQuery.trim()) return;

    if (page === 1) {
      setSearchLoading(true);
      setSearchResults([]);
      setSearchPage(1);
    } else {
      setIsLoadingMore(true);
    }

    try {
      // Build search query with filters
      let query = searchQuery;
      if (orientation !== "all" && searchType !== "videos") {
        query += ` orientation:${orientation}`;
      }
      if (color !== "all") {
        query += ` color:${color}`;
      }

      const perPage = searchType === "all" ? 20 : 40; // Smaller page size when fetching both

      if (searchType === "all") {
        // Search both images and videos
        const [photosResponse, videosResponse] = await Promise.all([
          mediaService.searchPhotos(query, page, perPage),
          mediaService.searchVideos(query, page, perPage)
        ]);

        const allResults = [
          ...(photosResponse.photos || []),
          ...(videosResponse.videos || [])
        ];

        if (append) {
          setSearchResults(prev => [...prev, ...allResults]);
        } else {
          setSearchResults(allResults);
        }

        // Use the larger total for results count
        const totalPhotos = photosResponse.total_results || 0;
        const totalVideos = videosResponse.total_results || 0;
        setTotalResults(totalPhotos + totalVideos);
        setHasMoreResults(!!photosResponse.next_page || !!videosResponse.next_page);
      } else if (searchType === "images") {
        const response = await mediaService.searchPhotos(query, page, perPage);
        if (append) {
          setSearchResults(prev => [...prev, ...(response.photos || [])]);
        } else {
          setSearchResults(response.photos || []);
        }
        setTotalResults(response.total_results);
        setHasMoreResults(!!response.next_page);
      } else {
        const response = await mediaService.searchVideos(query, page, perPage);
        if (append) {
          setSearchResults(prev => [...prev, ...(response.videos || [])]);
        } else {
          setSearchResults(response.videos || []);
        }
        setTotalResults(response.total_results);
        setHasMoreResults(!!response.next_page);
      }

      setSearchPage(page);
    } catch (error) {
      console.error("Failed to search media providers:", error);
    } finally {
      setSearchLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Load more results
  const loadMoreResults = () => {
    if (!isLoadingMore && hasMoreResults) {
      searchPexels(searchPage + 1, true);
    }
  };

  const handleReplaceAsset = (
    assetId: string,
    assetType: "image" | "video",
  ) => {
    setReplacingAsset({ id: assetId, type: assetType });
    setSearchType("all"); // Default to showing all assets for maximum flexibility
    setSearchQuery("");
    setSearchResults([]);
    setSelectedAsset(null);
    setSearchPage(1);
    setTotalResults(0);
    setHasMoreResults(false);
    setOrientation("all");
    setColor("all");
    setIsReplaceModalOpen(true);
  };

  const refreshSlidePreview = useCallback(() => {
    if (iframeRef.current && slide) {
      // Force iframe to reload by updating its src
      const iframe = iframeRef.current;
      iframe.srcdoc = slide.content;
    }
  }, [slide]);

  const confirmReplacement = async () => {
    if (!selectedAsset || !replacingAsset || !conversationId || !slide) return;

    try {
      // Get slide number
      const slideNumber = extractSlideNumber(slide.path);
      
      // Determine if the selected asset is an image or video
      const isSelectedImage = 'src' in selectedAsset.asset;
      
      // Get the new asset URL
      const assetUrl = isSelectedImage
        ? (selectedAsset.asset as MediaPhoto).src.original
        : (selectedAsset.asset as MediaVideo).video_files[0]?.link || "";
      
      // Determine the new asset type
      const newAssetType = isSelectedImage ? "image" : "video";
      
      // Check if we're changing asset type
      const isTypeChange = replacingAsset.type !== newAssetType;
      
      // Format the message based on whether we're changing types
      let message;
      if (isTypeChange) {
        // Cross-type replacement: e.g., replacing image with video or vice versa
        message = `Replace the ${replacingAsset.type} in slide--${slideNumber.padStart(3, '0')} with id: ${replacingAsset.id} with a ${newAssetType} from this URL: ${assetUrl}. Update the HTML structure accordingly (convert ${replacingAsset.type === "image" ? "<img> or background-image" : "<video>"} to ${newAssetType === "image" ? "<img> or background-image" : "<video>"}) and git commit the changes once done`;
      } else {
        // Same-type replacement
        message = `Update the ${replacingAsset.type} in slide--${slideNumber.padStart(3, '0')} for the id: ${replacingAsset.id} with the new ${replacingAsset.type} from this URL: ${assetUrl} and git commit the changes once done`;
      }
      
      // Send message to chat
      await sendMessage({
        conversationId,
        content: message,
      });

      // Reset states and close modal
      setSelectedAsset(null);
      setReplacingAsset(null);
      setSearchResults([]);
      setSearchQuery("");
      setIsReplaceModalOpen(false);
      
      // Wait for stream completion before refreshing
      await waitForStreamCompletion(conversationId);
      
    } catch (error) {
      console.error("Failed to send replacement message:", error);
    }
  };

  /**
   * Wait for stream completion before refreshing slide
   */
  const waitForStreamCompletion = async (conversationId: string) => {
    const maxWaitTime = 60000; // 60 seconds max wait
    const checkInterval = 1000; // Check every 1 second
    const startTime = Date.now();
    
    return new Promise<void>((resolve) => {
      const checkCompletion = () => {
        const hasActiveStream = streamManager.hasActiveStream(conversationId);
        const elapsedTime = Date.now() - startTime;
        
        if (!hasActiveStream) {
          // Stream completed, wait a bit more for processing then refresh
          setTimeout(() => {
            refreshSlidePreview();
            resolve();
          }, 3000); // 3 second delay after stream completion
        } else if (elapsedTime >= maxWaitTime) {
          // Timeout reached, refresh anyway
          console.warn('⏰ Stream completion timeout reached, refreshing slide anyway');
          refreshSlidePreview();
          resolve();
        } else {
          // Continue waiting
          setTimeout(checkCompletion, checkInterval);
        }
      };
      
      // Start checking
      checkCompletion();
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-muted-foreground">Loading scene...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-lg text-destructive">Error: {error}</div>
        {onBackToGallery && (
          <Button onClick={handleBackToGallery} variant="outline">
            Back to Gallery
          </Button>
        )}
      </div>
    );
  }

  if (!slide) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-lg text-muted-foreground">Slide not found</div>
        {onBackToGallery && (
          <Button onClick={handleBackToGallery} variant="outline">
            Back to Gallery
          </Button>
        )}
      </div>
    );
  }

  // Handler for applying template
  const handleApplyTemplate = async () => {
    if (!isTemplate || !conversationId || !slide) {
      console.warn('⚠️ Cannot apply template: missing required data');
      return;
    }

    try {
      // Extract template ID from slide data
      const templateId = slide.id;
      console.log('📋 Applying template:', templateId);

      // Call API to apply template
      await templateAPI.applyTemplate(conversationId, templateId);
      console.log('✅ Template applied successfully');

      // Navigate to gallery layouts tab
      navigate({
        to: '/chat/$conversationId/gallery',
        params: { conversationId },
        search: { tab: 'layouts' } as any
      });
    } catch (error) {
      console.error('❌ Failed to apply template:', error);
      setError(error instanceof Error ? error.message : 'Failed to apply template');
    }
  };

  // Render GSAP slide preview if this is a GSAP slide
  console.log('🎨 SlidePreview - Render decision:', {
    isGsapSlide,
    hasGsapSlide: !!gsapSlide,
    willRenderGsap: isGsapSlide && gsapSlide,
    slideId: slide?.id
  });

  if (isGsapSlide && gsapSlide) {
    console.log('✅ SlidePreview - Rendering GsapSlidePreviewEnhanced component');
    return (
      <GsapSlidePreviewEnhanced
        slide={gsapSlide}
        conversationId={conversationId}
        onBackToGallery={onBackToGallery}
        isTemplate={isTemplate}
        onApplyTemplate={isTemplate ? handleApplyTemplate : undefined}
        className={className}
      />
    );
  }

  if (isGsapSlide && !gsapSlide) {
    console.log('⚠️ SlidePreview - GSAP slide loading, showing spinner');
    return (
      <div className={`h-full flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/5 ${className}`}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-sm text-muted-foreground">Loading GSAP animation...</p>
        </div>
      </div>
    );
  }

  // Render HTML slide preview (existing implementation)
  return (
    <div
      className={`h-full flex flex-col bg-gradient-to-br from-background via-background to-muted/5 ${className}`}
    >
      {/* Minimal Top Bar */}
      <div className="flex-shrink-0 bg-background/80 backdrop-blur-md border-b border-border/30">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToGallery && (
                <Button
                  onClick={handleBackToGallery}
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs hover:bg-accent/50 transition-colors"
                >
                  <svg
                    className="h-3.5 w-3.5 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 17l-5-5m0 0l5-5m-5 5h12"
                    />
                  </svg>
                  Gallery
                </Button>
              )}
              <div className="text-sm font-medium text-foreground/90">
                {slideInfo?.title || extractTitle(slide.content)}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Manual Refresh Button */}
              <Button
                onClick={reloadSlideContent}
                disabled={isRefreshing}
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                title="Refresh slide content"
              >
                <svg
                  className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </Button>

              {slideInfo && (
                <Button
                  onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                  variant={isDrawerOpen ? "default" : "ghost"}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  title="Toggle slide details"
                >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Slide Content - Maximized */}
      <div className="flex-1 overflow-hidden relative flex flex-col">
        {/* Visual Content - Full Height with min-height constraint */}
        <div
          className={`flex-1 bg-gradient-to-br from-muted/5 via-background to-muted/10 transition-all duration-300 ${isDrawerOpen ? "mr-96" : "mr-0"}`}
          style={{ minHeight: 'calc(100vh - 200px)' }}
        >
          <div className="h-full flex items-center justify-center p-4">
            <div className="relative group w-full h-full flex items-center justify-center">
              {/* Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Slide Container */}
              <div
                ref={containerRef}
                className="relative bg-white shadow-2xl rounded-xl overflow-hidden border border-border/30 ring-1 ring-black/5"
                style={{
                  width: `${resolution.width * scale}px`,
                  height: `${resolution.height * scale}px`,
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
              >
                {iframeReady ? (
                  <iframe
                    ref={iframeRef}
                    srcDoc={slide.content}
                    className="absolute border-0 transition-opacity duration-300"
                    title={extractTitle(slide.content)}
                    sandbox="allow-same-origin allow-scripts"
                    style={{
                      width: `${resolution.width}px`,
                      height: `${resolution.height}px`,
                      transform: `scale(${scale})`,
                      transformOrigin: "top left",
                      left: "0",
                      top: "0",
                      opacity: 1,
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/10">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Loading slide...</p>
                    </div>
                  </div>
                )}

                {/* Corner Indicators */}
                <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-primary/30 rounded-tl-sm" />
                <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-primary/30 rounded-tr-sm" />
                <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-primary/30 rounded-bl-sm" />
                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-primary/30 rounded-br-sm" />

                {/* Refresh Indicator */}
                {isRefreshing && (
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1.5 rounded-md flex items-center gap-2 animate-in fade-in duration-200">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-xs">Updating...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Navigation Bar */}
        <div className="flex-shrink-0 bg-background/95 backdrop-blur-md border-t border-border/50 shadow-lg">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between">
              {/* Slide Info */}
              <div className="flex items-center gap-4">
                <div className="text-sm font-medium text-foreground">
                  Scene {extractSlideNumber(slide.path)} of {allSlides.length}
                </div>
                {lastUpdated && (
                  <div className="text-xs text-muted-foreground">
                    Updated: {lastUpdated.toLocaleTimeString()}
                  </div>
                )}
                {slideInfo?.start_time !== undefined &&
                  slideInfo?.end_time !== undefined && (
                    <div className="flex items-center gap-1.5 text-sm text-primary font-medium bg-primary/10 rounded-full px-3 py-1">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {formatTime(slideInfo.end_time - slideInfo.start_time)}
                    </div>
                  )}
              </div>

              {/* Navigation Controls - Disabled to prevent infinite loop 
                  Users should navigate through gallery view */}
              {/* <div className="flex items-center gap-4">
                <Button
                  onClick={goToPrevious}
                  disabled={!canGoToPrevious()}
                  variant="outline"
                  size="sm"
                  className="h-9 px-4 hover:bg-accent transition-colors disabled:opacity-40"
                >
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Previous
                </Button>

                <Button
                  onClick={goToNext}
                  disabled={!canGoToNext()}
                  variant="outline"
                  size="sm"
                  className="h-9 px-4 hover:bg-accent transition-colors disabled:opacity-40"
                >
                  Next
                  <svg
                    className="h-4 w-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>

                <div className="text-xs text-muted-foreground hidden lg:flex items-center gap-2 ml-4">
                  <div className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted/50 rounded">
                      ←
                    </kbd>
                    <kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted/50 rounded">
                      →
                    </kbd>
                  </div>
                  <span>Navigate</span>
                </div>
              </div> */}
            </div>
          </div>
        </div>

        {/* Enhanced Sliding Drawer */}
        <div
          className={`absolute top-0 right-0 h-full w-96 bg-gradient-to-b from-background via-background to-background/95 backdrop-blur-xl border-l border-border/50 shadow-2xl transform transition-all duration-300 ease-in-out z-20 flex flex-col ${isDrawerOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* Drawer Header - Modern Design */}
          <div className="flex-shrink-0 bg-card/50 backdrop-blur-sm border-b border-border/50">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold flex items-center gap-2">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  Scene Details
                </h2>
                <Button
                  onClick={() => setIsDrawerOpen(false)}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-slate-200 dark:hover:bg-slate-700"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </Button>
              </div>
            </div>
          </div>

          {/* Drawer Content with Tabs */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <Tabs
              value={drawerTab}
              onValueChange={(val) => setDrawerTab(val as "info" | "assets")}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <TabsList
                className="grid w-full grid-cols-2 h-9 mx-4 mt-2"
                style={{ width: "calc(100% - 2rem)" }}
              >
                <TabsTrigger
                  value="info"
                  className="text-xs h-7 flex items-center gap-1.5"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Info
                </TabsTrigger>
                <TabsTrigger
                  value="assets"
                  className="text-xs h-7 flex items-center gap-1.5"
                >
                  <Image className="w-3.5 h-3.5" />
                  Assets
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="info"
                className="flex-1 mt-3 overflow-y-auto px-4 pb-4"
              >
                {infoLoading && (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-sm text-muted-foreground">
                      Loading scene details...
                    </div>
                  </div>
                )}
                {!infoLoading && slideInfo && (
                  <div className="space-y-4">
                    {/* Enhanced Info Cards */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-3 border border-primary/20">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-primary/20 rounded-md flex items-center justify-center">
                            <svg
                              className="w-3.5 h-3.5 text-primary"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4"
                              />
                            </svg>
                          </div>
                          <div className="text-xs text-primary/70 font-medium">
                            Scene
                          </div>
                        </div>
                        <div className="text-sm font-bold">
                          {slideInfo.scene_id}
                        </div>
                      </div>
                      <div className="rounded-xl p-3 border border-primary/20 bg-primary/10">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-primary/20 rounded-md flex items-center justify-center">
                            <svg
                              className="w-3.5 h-3.5 text-primary"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                              />
                            </svg>
                          </div>
                          <div className="text-xs text-primary font-medium">
                            Layout
                          </div>
                        </div>
                        <div className="text-sm font-bold capitalize">
                          {(slideInfo.layout_type || "")
                            .replace("layout_", "")
                            .replace("_", " ")}
                        </div>
                      </div>
                    </div>

                    {/* Timing Info Card */}
                    {slideInfo.start_time !== undefined &&
                      slideInfo.end_time !== undefined && (
                        <div className="rounded-xl p-4 border border-primary/20 bg-primary/10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                                <svg
                                  className="w-4 h-4 text-primary"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="text-xs text-primary font-medium">
                                  Timeline
                                </div>
                                <div className="text-sm font-semibold">
                                  {formatTime(slideInfo.start_time)} -{" "}
                                  {formatTime(slideInfo.end_time)}
                                </div>
                              </div>
                            </div>
                            <div className="bg-primary/20 rounded-full px-3 py-1.5">
                              <span className="text-xs font-bold text-primary">
                                {formatTime(
                                  slideInfo.end_time - slideInfo.start_time,
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                    {/* Script Section */}
                    <div className="bg-card rounded-lg border shadow-sm p-4">
                      <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Script
                      </h3>
                      <div className="text-sm bg-muted/20 rounded-lg p-3 leading-relaxed">
                        {slideInfo.script_text}
                      </div>
                    </div>

                    {/* Visual Instructions Section */}
                    <div className="bg-card rounded-lg border shadow-sm p-4">
                      <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Visual Instructions
                      </h3>
                      <div className="text-sm bg-muted/20 rounded-lg p-3">
                        {slideInfo.visual_instructions}
                      </div>
                      {slideInfo.content_description && (
                        <div className="mt-3">
                          <h4 className="text-xs font-medium text-muted-foreground mb-2">
                            Content Description
                          </h4>
                          <div className="text-sm bg-muted/10 rounded p-2">
                            {typeof slideInfo.content_description === 'string' 
                              ? slideInfo.content_description 
                              : JSON.stringify(slideInfo.content_description, null, 2)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Metadata Footer */}
                    {slideInfo.generation_metadata && (
                      <div className="bg-muted/10 rounded-lg p-3 border border-border/30">
                        <div className="space-y-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span>
                              Modified:{" "}
                              {new Date(
                                slideInfo.generation_metadata.last_modified,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          {slideInfo.generation_metadata.timing_updated && (
                            <div className="ml-5">
                              Timing:{" "}
                              {new Date(
                                slideInfo.generation_metadata.timing_updated,
                              ).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {!infoLoading && !slideInfo && (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-sm text-muted-foreground">
                      No scene details available
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent
                value="assets"
                className="flex-1 mt-3 overflow-y-auto p-4"
              >
                <div className="h-full flex flex-col space-y-4">
                  {/* Existing Assets Section */}
                  {slideInfo &&
                  ((slideInfo.image_urls?.length &&
                    slideInfo.image_urls?.length > 0) ||
                    (slideInfo.video_urls?.length &&
                      slideInfo.video_urls?.length > 0)) ? (
                    <>
                      {/* Images */}
                      {slideInfo.image_urls &&
                        slideInfo.image_urls.length > 0 && (
                          <div>
                            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                              <Image className="w-4 h-4" />
                              Images ({slideInfo.image_urls.length})
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                              {slideInfo.image_urls.map((img, index) => (
                                <div
                                  key={img.image_id}
                                  className="group relative"
                                >
                                  <div className="aspect-video bg-muted/20 rounded-lg overflow-hidden border border-border/50 group-hover:border-primary/50 transition-colors">
                                    <img
                                      src={img.image_url}
                                      alt={`Image ${index + 1}`}
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                    />
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2 bg-black/50 text-white hover:bg-black/70"
                                    onClick={() =>
                                      handleReplaceAsset(img.image_id, "image")
                                    }
                                  >
                                    <Replace className="w-3 h-3 mr-1" />
                                    Replace
                                  </Button>
                                  <div className="text-xs text-muted-foreground mt-1 truncate font-mono" title={img.image_id}>
                                    {img.image_id}
                                  </div>
                                  {/* Show if asset has generation prompt */}
                                  {slideInfo.generated_assets?.find(a => a.asset_component_id === img.image_id) && (
                                    <div className="text-xs text-primary/60 mt-0.5 flex items-center gap-1">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                      </svg>
                                      AI Generated
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Videos */}
                      {slideInfo.video_urls &&
                        slideInfo.video_urls.length > 0 && (
                          <div className="mt-4">
                            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                              <Video className="w-4 h-4" />
                              Videos ({slideInfo.video_urls.length})
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                              {slideInfo.video_urls.map((vid) => (
                                <div
                                  key={vid.video_id}
                                  className="group relative"
                                >
                                  <div className="aspect-video bg-muted/20 rounded-lg overflow-hidden border border-border/50 group-hover:border-primary/50 transition-colors">
                                    <video
                                      src={vid.video_url}
                                      className="w-full h-full object-cover"
                                      controls
                                    />
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2 bg-black/50 text-white hover:bg-black/70"
                                    onClick={() =>
                                      handleReplaceAsset(vid.video_id, "video")
                                    }
                                  >
                                    <Replace className="w-3 h-3 mr-1" />
                                    Replace
                                  </Button>
                                  <div className="text-xs text-muted-foreground mt-1 truncate font-mono" title={vid.video_id}>
                                    {vid.video_id}
                                  </div>
                                  {/* Show if asset has generation prompt */}
                                  {slideInfo.generated_assets?.find(a => a.asset_component_id === vid.video_id) && (
                                    <div className="text-xs text-primary/60 mt-0.5 flex items-center gap-1">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                      </svg>
                                      AI Generated
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-32">
                      <div className="text-sm text-muted-foreground">
                        No assets in this slide
                      </div>
                    </div>
                  )}

                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Asset Replacement Modal */}
      <Dialog open={isReplaceModalOpen} onOpenChange={setIsReplaceModalOpen}>
        <DialogContent className="max-w-7xl w-[95vw] h-[90vh] flex flex-col p-0">
          <div className="px-6 pt-6 pb-4 border-b">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center">
                  <Replace className="w-5 h-5 text-primary" />
                </div>
                Replace {replacingAsset?.type === "image" ? "Image" : "Video"}
              </DialogTitle>
              <DialogDescription className="mt-2">
                Replacing asset with ID: <span className="font-mono text-primary font-semibold">{replacingAsset?.id}</span>
                {slide && (
                  <span className="ml-2">in slide <span className="font-mono text-primary font-semibold">{extractSlideNumber(slide.path).padStart(3, '0')}</span></span>
                )}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 flex flex-col min-h-0 px-6 pb-6">
            {/* Search and Filters Section */}
            <div className="py-4 space-y-4 border-b">
              {/* Asset Type Toggle */}
              <Tabs value={searchType} onValueChange={(val) => setSearchType(val as "all" | "images" | "videos")} className="w-full">
                <TabsList className="grid w-full grid-cols-3 h-10">
                  <TabsTrigger value="all" className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    All
                  </TabsTrigger>
                  <TabsTrigger value="images" className="flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Images
                  </TabsTrigger>
                  <TabsTrigger value="videos" className="flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Videos
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Search Bar */}
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={`Search for ${searchType === "all" ? "images and videos" : searchType}... (e.g., "nature", "business", "technology")`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && searchPexels(1)}
                    className="pl-12 h-12 text-base bg-background/50 border-border/50 focus:bg-background"
                    autoFocus
                  />
                </div>
                <Button
                  onClick={() => searchPexels(1)}
                  disabled={searchLoading || !searchQuery.trim()}
                  className="h-12 px-8"
                  size="lg"
                >
                  {searchLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Search
                    </>
                  )}
                </Button>
              </div>

              {/* Filters Row - Show for images and "all" mode */}
              {searchType !== "videos" && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Filter className="w-4 h-4" />
                    <span className="font-medium">Filters:</span>
                  </div>
                  
                  {/* Orientation Filter */}
                  <Select value={orientation} onValueChange={(val: any) => setOrientation(val)}>
                    <SelectTrigger className="w-[140px] h-9">
                      <SelectValue placeholder="Orientation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Orientations</SelectItem>
                      <SelectItem value="landscape">Landscape</SelectItem>
                      <SelectItem value="portrait">Portrait</SelectItem>
                      <SelectItem value="square">Square</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Color Filter */}
                  <Select value={color} onValueChange={setColor}>
                    <SelectTrigger className="w-[140px] h-9">
                      <SelectValue placeholder="Color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Colors</SelectItem>
                      <SelectItem value="red">Red</SelectItem>
                      <SelectItem value="orange">Orange</SelectItem>
                      <SelectItem value="yellow">Yellow</SelectItem>
                      <SelectItem value="green">Green</SelectItem>
                      <SelectItem value="turquoise">Turquoise</SelectItem>
                      <SelectItem value="blue">Blue</SelectItem>
                      <SelectItem value="violet">Violet</SelectItem>
                      <SelectItem value="pink">Pink</SelectItem>
                      <SelectItem value="brown">Brown</SelectItem>
                      <SelectItem value="black">Black</SelectItem>
                      <SelectItem value="gray">Gray</SelectItem>
                      <SelectItem value="white">White</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Apply Filters Button */}
                  {(orientation !== "all" || color !== "all") && (
                    <Button
                      onClick={() => searchPexels(1)}
                      variant="secondary"
                      size="sm"
                      className="h-9"
                    >
                      Apply Filters
                    </Button>
                  )}

                  {/* Results Counter */}
                  {totalResults > 0 && (
                    <div className="ml-auto text-sm text-muted-foreground">
                      Showing <span className="font-semibold text-foreground">{searchResults.length}</span> of{" "}
                      <span className="font-semibold text-foreground">{totalResults.toLocaleString()}</span> results
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-hidden mt-4">
              {searchResults.length > 0 ? (
                <div className="h-full overflow-y-auto" ref={modalScrollRef}>
                  <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 py-2">
                    {searchResults.map((result: any) => (
                      <div
                        key={result.id}
                        className={`group relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-200 hover:shadow-xl hover:-translate-y-1 ${
                          selectedAsset?.asset.id === result.id
                            ? "border-primary ring-4 ring-primary/30 shadow-xl scale-[0.98]"
                            : "border-border hover:border-primary/60"
                        }`}
                        onClick={() => {
                          // Detect actual asset type from the result object
                          const assetType = 'src' in result ? 'image' : 'video';
                          handleAssetSelect(result, assetType);
                        }}
                      >
                        <div className="aspect-[4/3] bg-gradient-to-br from-muted/20 to-muted/10 relative">
                          {/* Detect if result is an image or video based on its properties */}
                          {'src' in result ? (
                            <>
                              <img
                                src={(result as MediaPhoto).src.large}
                                alt={(result as MediaPhoto).alt || "Media image"}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                loading="lazy"
                              />
                              {/* Gradient overlay on hover */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </>
                          ) : (
                            <div
                              className="relative w-full h-full group"
                              onMouseEnter={() => {
                                const video = document.querySelector(`[data-video-id="${result.id}"]`) as HTMLVideoElement;
                                if (video && video.readyState >= 2) {
                                  video.play().catch(console.error);
                                }
                              }}
                              onMouseLeave={() => {
                                const video = document.querySelector(`[data-video-id="${result.id}"]`) as HTMLVideoElement;
                                if (video) {
                                  video.pause();
                                  video.currentTime = 0;
                                }
                              }}
                            >
                              {/* Video element - hidden by default, shown on hover */}
                              <video
                                data-video-id={result.id}
                                src={(result as MediaVideo).video_files?.find(f => f.quality === 'sd' || f.quality === 'hd')?.link || ''}
                                className="w-full h-full object-cover absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                muted
                                loop
                                playsInline
                                preload="metadata"
                              />

                              {/* Thumbnail - shown by default, hidden on hover */}
                              <img
                                src={(result as MediaVideo).image}
                                alt="Video thumbnail"
                                className="w-full h-full object-cover transition-all duration-500 group-hover:opacity-0"
                                loading="lazy"
                              />

                              {/* Video play overlay - hidden on hover */}
                              <div className="absolute inset-0 flex items-center justify-center opacity-100 group-hover:opacity-0 transition-opacity duration-500">
                                <div className="w-14 h-14 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg">
                                  <Video className="w-7 h-7 text-gray-800 ml-1" />
                                </div>
                              </div>

                              {/* Duration badge */}
                              <div className="absolute bottom-2 right-2 bg-black/90 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-full font-medium z-10">
                                {Math.floor((result as MediaVideo).duration / 60)}:{String((result as MediaVideo).duration % 60).padStart(2, '0')}
                              </div>

                              {/* HD Badge for high quality videos */}
                              {(result as MediaVideo).video_files?.some(f => f.quality === 'hd') && (
                                <div className="absolute top-2 left-2 bg-primary/90 backdrop-blur-md text-white text-xs px-2 py-0.5 rounded font-bold z-10">
                                  HD
                                </div>
                              )}

                              {/* Loading indicator for video */}
                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                <div className="bg-black/70 backdrop-blur-md text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                  <span>Preview</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Selection indicator */}
                          {selectedAsset?.asset.id === result.id && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center animate-in zoom-in-50 duration-200">
                                <svg className="w-7 h-7 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          )}

                          {/* Hover info overlay */}
                          <div className="absolute bottom-0 left-0 right-0 p-3 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                            <p className="text-sm font-medium truncate">
                              {'src' in result
                                ? (result as MediaPhoto).photographer
                                : (result as MediaVideo).user?.name
                              }
                            </p>
                            <p className="text-xs opacity-90">
                              {'src' in result
                                ? `${(result as MediaPhoto).width} × ${(result as MediaPhoto).height}`
                                : `${(result as MediaVideo).width} × ${(result as MediaVideo).height}`
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Load More Indicator / Infinite Scroll Trigger */}
                  {hasMoreResults && (
                    <div ref={loadMoreRef} className="py-8 flex justify-center">
                      {isLoadingMore ? (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm font-medium">Loading more...</span>
                        </div>
                      ) : (
                        <Button
                          onClick={loadMoreResults}
                          variant="outline"
                          className="min-w-[200px]"
                        >
                          Load More Results
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : searchQuery.trim() && !searchLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-muted/30 to-muted/10 rounded-full flex items-center justify-center mx-auto">
                      <Search className="w-10 h-10 text-muted-foreground" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-xl font-semibold text-foreground">
                        No {replacingAsset?.type === "image" ? "images" : "videos"} found
                      </p>
                      <p className="text-base text-muted-foreground max-w-md">
                        Try different keywords or adjust your filters
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center mt-4">
                        <span className="text-sm">Popular searches:</span>
                        {["nature", "business", "technology", "abstract", "people"].map((term) => (
                          <Button
                            key={term}
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSearchQuery(term);
                              searchPexels(1);
                            }}
                            className="h-7 text-xs"
                          >
                            {term}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : !searchQuery.trim() ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full flex items-center justify-center mx-auto animate-pulse">
                      <Search className="w-10 h-10 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-xl font-semibold">
                        Search for {replacingAsset?.type === "image" ? "Images" : "Videos"}
                      </p>
                      <p className="text-base text-muted-foreground max-w-md">
                        Enter keywords to find the perfect replacement from millions of free stock {replacingAsset?.type === "image" ? "photos" : "videos"}
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center mt-4">
                        <span className="text-sm text-muted-foreground">Try:</span>
                        {["modern office", "creative", "minimal", "colorful", "professional"].map((term) => (
                          <Button
                            key={term}
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSearchQuery(term);
                              searchPexels(1);
                            }}
                            className="h-7 text-xs hover:bg-primary/10"
                          >
                            {term}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Action Footer */}
            <div className="pt-4 border-t flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsReplaceModalOpen(false);
                    setReplacingAsset(null);
                    setSearchResults([]);
                    setSearchQuery("");
                    setSelectedAsset(null);
                    setOrientation("all");
                    setColor("all");
                  }}
                >
                  Cancel
                </Button>
                {selectedAsset && (
                  <div className="text-sm text-muted-foreground">
                    Selected: <span className="font-medium text-foreground">{selectedAsset.type === "image" ? (selectedAsset.asset as MediaPhoto).photographer : (selectedAsset.asset as MediaVideo).user?.name}</span>
                  </div>
                )}
              </div>
              
              {selectedAsset && (
                <Button
                  onClick={confirmReplacement}
                  size="lg"
                  className="min-w-[200px]"
                >
                  <Replace className="w-5 h-5 mr-2" />
                  {(() => {
                    const isSelectedImage = 'src' in selectedAsset.asset;
                    const newType = isSelectedImage ? "Image" : "Video";
                    const isTypeChange = replacingAsset?.type !== (isSelectedImage ? "image" : "video");
                    
                    if (isTypeChange) {
                      return `Replace with ${newType}`;
                    } else {
                      return `Update ${newType}`;
                    }
                  })()}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
